import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { Dayjs } from "dayjs";
import { FileSaverOptions, saveAs } from "file-saver";
import { IoCardOutline, IoSettingsOutline } from "react-icons/io5";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import Balances from "../../components/Billing/Balances";
import DueCharges from "../../components/Billing/DueCharges";
import NotificationAlert from "../../components/Billing/NotificationAlert";
import PastCharges from "../../components/Billing/PastCharges";
import StatementOfEntitlementModal from "../../components/Billing/StatementOfEntitlementModal";
import UpcomingCharges from "../../components/Billing/UpcomingCharges";
import { ButtonPrimary } from "../../components/Common/Buttons/Buttons";
import { CardLayout } from "../../layouts/Layouts";
import { useAppDispatch } from "../../store/hooks";
import { getAccountBalance, getCurrentAccountBalance } from "../../store/modules/accountBalance/accountBalanceActions";
import { downloadStatementOfEntitlement } from "../../store/modules/statementOfEntitlement/statementOfEntitlementApi";
import { initReport } from "../../store/modules/statementReport/statementReportActions";
import { RootState } from "../../store/store";
import { getStyleColor } from "../../utils/helpers";
import { useIsMaguireEnabled } from "../../utils/maguireHelper";

const useStyles = makeStyles()((theme) => ({
  root: {
    flexGrow: 1,
  },

  divider: {
    marginTop: theme.spacing(3),
  },
}));

function Billing() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const isMaguireEnabled = useIsMaguireEnabled();
  const isStatementOfEntitlementEnabled = useSelector(
    (state: RootState) => state.appConfigs.statementOfEntitlementEnabled
  );
  const soeButtonLabel = useSelector((state: RootState) => state.appConfigs.soeButtonLabel);
  const soeModalDescription = useSelector((state: RootState) => state.appConfigs.soeModalDescription);
  const [entitlementModalOpen, setEntitlementModalOpen] = useState(false);
  const [isGeneratingStatement, setIsGeneratingStatement] = useState(false);
  const [statementGenerationError, setStatementGenerationError] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  const customer_account_id = useSelector((state: RootState) => state.auth.user?.profile.customer_account_id);
  const balance = useSelector((state: RootState) => state.balance.balanceState.payload);
  const file = useSelector((state: RootState) => state.statementReport.payload);
  const isDownloaded = useSelector((state: RootState) => state.statementReport.isDownloaded);

  const currentBalance = useSelector((state: RootState) => state.balance.currentBalanceState.accountBalance);

  useEffect(() => {
    if (customer_account_id) {
      if (isMaguireEnabled) {
        if (!currentBalance) {
          getCurrentAccountBalance(dispatch, customer_account_id);
        }
      } else {
        if (!balance) {
          getAccountBalance(dispatch, customer_account_id);
        }
      }
    }
  }, [dispatch, customer_account_id, isMaguireEnabled, balance, currentBalance]);

  useEffect(() => {
    if (isDownloaded && file) {
      const options: FileSaverOptions = {
        autoBom: false,
      };

      saveAs(file, "report.pdf", options);
      initReport(dispatch);
    }
  }, [file, isDownloaded]);

  const handleGenerateStatementOfEntitlement = (startDate: Dayjs, endDate: Dayjs | null) => {
    if (!customer_account_id || !endDate) {
      return;
    }

    setIsGeneratingStatement(true);
    setStatementGenerationError(null);
    downloadStatementOfEntitlement(customer_account_id, startDate, endDate)
      .then((blob) => {
        saveAs(blob, "statement-of-entitlement.pdf");
        setEntitlementModalOpen(false);
      })
      .catch((error) => {
        console.error("Error occurred while downloading statement of entitlement", error);
        setStatementGenerationError("Something went wrong generating your statement. Please try again.");
      })
      .finally(() => {
        setIsGeneratingStatement(false);
      });
  };

  return (
    <CardLayout>
      <CardLayout.Header bgColor="white">
        <Grid container alignItems="center" spacing={1}>
          <Grid item xs={2}>
            <h1 className={`h2`}>Billing</h1>
          </Grid>
          <Grid item xs={8}>
            <Balances />
          </Grid>
          <Grid item xs={2}>
            <Grid container alignItems="center" justifyContent="flex-end" flexWrap="wrap-reverse">
              <Box marginRight={1}>
                <IconButton onClick={() => navigate("/payment-method")}>
                  <IoCardOutline
                    title="Update Payment Method"
                    size={32}
                    color={getStyleColor("--custom-color-dark-grey")}
                  />
                </IconButton>
              </Box>
              <ButtonPrimary onClick={() => navigate("/pay-now")}>
                <strong>Pay Now</strong>
              </ButtonPrimary>
            </Grid>
          </Grid>
        </Grid>
      </CardLayout.Header>
      <CardLayout.Body style={{ paddingTop: 0 }}>
        {isStatementOfEntitlementEnabled && (
          <>
            <Grid container>
              <Grid item>
                {/* span wrapper: Tooltip needs a ref-forwarding child, ButtonPrimary doesn't forward one */}
                <Tooltip
                  title={soeModalDescription}
                  placement="right"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "primary.main",
                        color: "white",
                        fontSize: 14,
                        borderRadius: "3px",
                        px: 1.5,
                        py: 1,
                        boxShadow: 3,
                      },
                    },
                    arrow: {
                      sx: {
                        color: "primary.main",
                      },
                    },
                    popper: {
                      modifiers: [{ name: "offset", options: { offset: [0, 8] } }],
                    },
                  }}
                >
                  <span>
                    <ButtonPrimary onClick={() => setEntitlementModalOpen(true)}>
                      <strong>{soeButtonLabel}</strong>
                    </ButtonPrimary>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
            <StatementOfEntitlementModal
              open={entitlementModalOpen}
              onClose={() => {
                setEntitlementModalOpen(false);
                setStatementGenerationError(null);
              }}
              onGenerate={handleGenerateStatementOfEntitlement}
              isSubmitting={isGeneratingStatement}
              errorMessage={statementGenerationError}
            />
          </>
        )}

        {/* <Divider className={classes.divider} style={{ marginTop: 0 }} /> */}
        <Grid container className={classes.root} spacing={3}>
          <Grid item xs={12}>
            <NotificationAlert />
          </Grid>
          <Grid item xs={12} md={6}>
            <DueCharges />
            <UpcomingCharges />
          </Grid>
          <Grid item xs={12} md={6}>
            <PastCharges />
          </Grid>
        </Grid>
      </CardLayout.Body>
    </CardLayout>
  );
}

export default Billing;
